import { ReactNode } from 'react';
import { useCommands, useLogger, useRedirectToMainApp, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { EmailConsentWithRedirectStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { ApiError, UnknownError } from '@packages/shared/core/common/errors';
import { useChat } from '@packages/shared/core/chat/hooks';
import { useExperts } from '@packages/shared/core/experts/hooks';
import { stringifyQueryParams } from '@packages/shared/core/common/utils/query';

type EmailConsentWithRedirectQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const EmailConsentWithRedirectQuizStep = ({ id, renderQuizScreen }: EmailConsentWithRedirectQuizStepProps) => {
  const logger = useLogger();
  const { analyticsService, chatService } = useServices();

  const { back, stepData, updateUserSettings, flowName } = useQuizStep(id);

  const { selectedExpert } = useExperts({ flowName });
  const { redirectToExpertsPage } = useRedirectToMainApp();
  const { createCommand } = useCommands();

  const expertId = selectedExpert ? selectedExpert.id : '';
  const { outgoingMessage } = useChat(expertId);

  const goBack = async () => back();

  const handleSubmit = async (isAgree: boolean) => {
    try {
      analyticsService.registrationEmailConsentClick(isAgree);
      await updateUserSettings(isAgree);
    } catch (err) {
      const error = err instanceof ApiError ? err : new UnknownError(err);
      logger.error(error);
    }
  };

  const goNext = async () => {
    if (!selectedExpert) {
      redirectToExpertsPage();
      return;
    }

    const { chatId } = await chatService.createChat(selectedExpert.id);

    const commandParams = stringifyQueryParams({
      chatId,
      text: outgoingMessage,
    });
    await createCommand(`sendMessage?${commandParams}`);
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        onSubmit: handleSubmit,
        goNext,
        ...(stepData as EmailConsentWithRedirectStepData),
      })}
    </>
  );
};

export default EmailConsentWithRedirectQuizStep;
